/* eslint-disable max-len */
import axios, { AxiosError } from 'axios';
import { Notification } from 'element-ui';
import * as amplitude from 'amplitude-js';

export const setAuthToken = (token: string) => localStorage.setItem('jwtToken', token);

export function getAuthToken() { return localStorage.getItem('jwtToken'); }

export function unsetAuthToken() { localStorage.removeItem('jwtToken'); }

export const getAssetUrl = (path: string): string => `${process.env.VUE_APP_ASSET_URL}${path}`;

function getMatchingItems(list: DataTransferItemList, acceptVal: string, multiple: boolean): DataTransferItem[] {
  const dataItems = Array.from(list);
  let results: DataTransferItem[];

  // Return the first item (or undefined) if our filter is for all files
  if (acceptVal === '') {
    results = dataItems.filter(item => item.kind === 'file');
    return (multiple) ? results : [results[0]];
  }

  // Split accepts values by ',' then by '/'. Trim everything & lowercase.
  const accepts = acceptVal.toLowerCase().split(',').map(accept => accept.split('/').map(part => part.trim())).filter(acceptParts => acceptParts.length === 2); // Filter invalid values

  const predicate = (item:DataTransferItem) => {
    if (item.kind !== 'file') return false;

    // 'Parse' the type.
    const [typeMain, typeSub] = item.type.toLowerCase().split('/').map(s => s.trim());

    // accepts.forEach(item => )

    // eslint-disable-next-line no-restricted-syntax
    for (const [acceptMain, acceptSub] of accepts) {
      // Look for an exact match, or a partial match if * is accepted, eg image/*.
      if (typeMain === acceptMain && (acceptSub === '*' || typeSub === acceptSub)) {
        return true;
      }
    }
    return false;
  };

  results = dataItems.filter(predicate);
  if (multiple === false) {
    results = [results[0]];
  }

  return results;
}

export function getFileData(data: DataTransfer, accept: string, multiple: boolean): File[] {
  const dragDataItems = getMatchingItems(data.items, accept, multiple);
  const files: File[] = [];

  // This is because Map doesn't like the null type returned by getAsFile
  dragDataItems.forEach((item) => {
    const file = item.getAsFile();
    if (file === null) return;
    files.push(file);
  });

  return files;
}

// Safari and Edge don't quite support extending Event, this works around it.
function fixExtendedEvent(instance: Event, type: Function) {
  if (!(instance instanceof type)) {
    Object.setPrototypeOf(instance, type.prototype);
  }
}

interface FileDropEventInit extends EventInit {
  action: FileDropAccept;
  files: File[];
}

type FileDropAccept = 'drop' | 'paste';

export class FileDropEvent extends Event {
  private _action: FileDropAccept;

  private _files: File[];

  constructor(typeArg: string, eventInitDict: FileDropEventInit) {
    super(typeArg, eventInitDict);
    fixExtendedEvent(this, FileDropEvent);
    this._files = eventInitDict.files;
    this._action = eventInitDict.action;
  }

  get action() {
    return this._action;
  }

  get files() {
    return this._files;
  }
}

export function filterObject<T>(obj: T) {
  return Object.entries(obj).reduce((acc, field) => {
    const [key, value] = field;
    return value ? { ...acc, [key]: value } : acc;
  }, {} as T);
}

const bindScroll = (callback: () => void) => {
  window.addEventListener('scroll', callback, false);
  window.addEventListener('resize', callback, false);
};

const unbindScroll = (callback: () => void) => {
  window.removeEventListener('scroll', callback, false);
  window.removeEventListener('resize', callback, false);
};

function getDocHeight() {
  return Math.max(
    document.body.scrollHeight, document.documentElement.scrollHeight,
    document.body.offsetHeight, document.documentElement.offsetHeight,
    document.body.clientHeight, document.documentElement.clientHeight,
  );
}

function getDocOffset() {
  return Math.floor(window.pageYOffset + window.innerHeight);
}

export function createScroller(getList: () => {
  loading: boolean; total: number | null, count: number,
}, callback: Function) {
  const scroll = () => {
    const {
      loading = true, total = null, count = 0,
    } = getList();
    const docOffset = getDocOffset();
    const docHeight = getDocHeight();
    if (!loading && (total === null || total > count)
      && docOffset > (docHeight - 100)) {
      // создаем новое событие подгрухки
      // eslint-disable-next-line no-console
      console.log('createScroller:event', {
        loading, total, count, docOffset, docHeight,
      });
      setTimeout(callback(), 10);
    }
  };
  bindScroll(scroll);
  return {
    remove: () => unbindScroll(scroll),
  };
}

export function throwError(title: string, message?: string) {
  return (error: AxiosError) => {
    const status = error && error.response ? error.response.status : 0;
    const msg = message
      || (error.response && error.response.data ? error.response.data.message : null)
      || error.message || 'Непредвиденная ошибка.';
    if ((status !== 401 && status <= 500) || status === 555) {
      // eslint-disable-next-line no-console
      console.error('catchError', status, title, error ? error.response : '', error);
      // Глобальные ошибки 401, 500 обрабатываются в перехватчике src/axios.js
      Notification({
        title,
        message: msg,
        type: 'error',
      });
    }
    amplitude.getInstance().logEvent(`Ошибка: ${title}`, msg);
    throw error || new Error(message);
  };
}

export function isBetween(value: number, min: number, max: number, boundaries = true) {
  return boundaries ? (min <= value) && (value <= max) : (min < value) && (value < max);
}

export function isRangeCrossed(target: [number, number], array: [number, number], boundaries = true) {
  return (isBetween(target[0], array[0], array[1], boundaries) || isBetween(target[1], array[0], array[1], boundaries))
    || (isBetween(array[0], target[0], target[1], boundaries) || isBetween(array[1], target[0], target[1], boundaries));
}

export function fileReader(file: File) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result);
    };

    reader.onerror = (e) => {
      reject(e);
    };

    reader.readAsArrayBuffer(file);
  });
}

export function isNotNullOrUndefined<T>(input: null | undefined | T): input is T {
  return input != null;
}

export async function uploadImage(file: File, clientId: number) {
  try {
    const binary = await fileReader(file);
    const { data: url } = await axios.post<string>(`https://rest.styleconcierge.ru/api/looks/uploadClientImg/${clientId}/${file.name}`, binary, {
      headers: { 'Content-Type': 'application/octet-stream', Authorization: `Bearer ${getAuthToken()}` },
    });
    return url;
  } catch (e) {
    return throwError('Ошибка загрузки картинки')(e);
  }
}

export function hasAuthority(userAuthorities: string[] = [], allowAuthorities: string[] = []) {
  return allowAuthorities && allowAuthorities.length > 0
    // правила разрешения для пункта заданы, проверяем наличие хотя бы одного совпадения
    ? userAuthorities.some(name => allowAuthorities.indexOf(name) >= 0)
    // правила разрешения для пункта не заданы, значит разрешаем при любом userAuthorities
    : true;
}
